import { Link } from '@tanstack/react-router';
import { memo, useCallback } from 'react';
import { BountyCompletion } from '~/util/types/bounties';
import { PackCompletion } from '~/util/types/packs';
import { UserProfilePicture } from './UserProfilePicture';
import { useLocaleDateTimeString } from '~/util/hooks/useLocale';
import { VirtualizedScrollArea } from './VirtualizedScrollArea';
import { cn } from '~/util';

type Completion = PackCompletion | BountyCompletion;

const CompletedByUserEntry = memo(
    ({
        completion,
        completedAt,
        index,
        showTrophies,
    }: {
        completion: Completion;
        completedAt: string;
        index: number;
        showTrophies: boolean;
    }) => (
        <Link
            to={'/profile/user/$userid'}
            params={{ userid: completion.user.username }}
            className="flex justify-between items-center p-2 rounded-xl hover:bg-background-semilight transition-colors clamp-[text-xs-base-clamp]"
        >
            <div className="flex items-center gap-4 ">
                {showTrophies && (
                    <div className="clamp-[min-w-4-16-clamp] clamp-[w-8-16-clamp] shrink-0">
                        {index === 0 ? (
                            <img src="/assets/top1.webp" alt="Top 1" />
                        ) : index === 1 ? (
                            <img
                                src="/assets/top2.webp"
                                alt="Top 2"
                                className="clamp-[w-6-12-clamp] m-auto"
                            />
                        ) : index === 2 ? (
                            <img
                                src="/assets/top3.webp"
                                alt="Top 3"
                                className="clamp-[w-4-8-clamp] m-auto"
                            />
                        ) : (
                            <span />
                        )}
                    </div>
                )}
                <UserProfilePicture
                    user={completion.user}
                    className="clamp-[size-10-16-clamp]"
                />
                <p className="clamp-[text-xs-base-clamp]">
                    {completion.user.global_name}
                </p>
            </div>
            <p className="text-secondary-light text-right clamp-[text-xs-base-clamp]">
                {completedAt}
            </p>
        </Link>
    ),
);
CompletedByUserEntry.displayName = 'CompletedByUserEntry';

export const CompletedByUsers = ({
    completions,
    showTrophies = false,
    scrollElement,
    className,
}: {
    completions: Completion[];
    showTrophies?: boolean;
    scrollElement?: HTMLElement | null;
    className?: string;
}) => {
    const localeDateTime = useLocaleDateTimeString();

    const getItemKey = useCallback(
        (index: number) =>
            `${completions[index].user.id}-${completions[index].completed_at}`,
        [completions],
    );

    return (
        <VirtualizedScrollArea
            items={completions}
            estimateSize={() => 80}
            renderItem={(completion, index) => (
                <CompletedByUserEntry
                    completion={completion}
                    completedAt={localeDateTime(completion.completed_at)}
                    index={index}
                    showTrophies={showTrophies}
                />
            )}
            overscan={10}
            getItemKey={getItemKey}
            className={cn('min-h-0 w-full', className)}
            scrollElement={scrollElement}
        />
    );
};
